import { faTimer } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  displayHumanReadableTemplateName,
  formatTime,
} from '@repo/videa-notes';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

import {
  SortableTable,
  type TableColumn,
} from '../../common-components/composed/sortableTable/SortableTable';
import type { Appointment } from '../../react-query/useAppointments';
import type { ClinicalNote } from '../../react-query/videa-notes/useExistingNotes';
import { useHasWriteback } from '../../react-query/videa-notes/useHasWriteback';
import { useSubmitWriteback } from '../../react-query/videa-notes/useSubmitWriteback';
import { capitalize } from '../../utils/string';
import type { TTreatmentResponse } from '../claims/freeClaims/insuranceExport/types';
import { useFormatAppointmentDate } from '../patientList/helpers';
import { PatientInfo } from '../patientList/patientStrip/patientStripRedesigned/patientInfoSheet/PatientInfo';
import { PatientListPageLayoutContextMap } from '../patientList/types';
import {
  appointmentDataAtom,
  isRecordingSessionActiveAtom,
} from '../videaNotes/state';
import type { VideaNotesAppointment } from '../videaNotes/helpers';
import { useOpenVideaNotes } from '../videaNotes/helpers';

import { AmbientTableActions } from './AmbientTableActions';

type SortDirection = 'asc' | 'desc';

interface AmbientTableProps {
  appointments: (VideaNotesAppointment & {
    treatments: TTreatmentResponse[];
    notes: ClinicalNote[];
  })[];
  sortField: string;
  sortDirection: SortDirection;
  onSort: (field: string, direction: SortDirection) => void;
  invalidateNotesCache: () => void;
  scrollContainerRef?: React.Ref<HTMLDivElement>;
  sentinelRef?: React.Ref<HTMLDivElement>;
  nextAppointmentRef?: React.Ref<HTMLTableRowElement>;
}

export const AmbientTable = ({
  appointments,
  sortField,
  sortDirection,
  onSort,
  invalidateNotesCache,
  scrollContainerRef,
  sentinelRef,
  nextAppointmentRef,
}: AmbientTableProps) => {
  const openVideaNotes = useOpenVideaNotes();

  const isRecordingSessionActive = useAtomValue(isRecordingSessionActiveAtom);
  const currentRecordingAppointment = useAtomValue(appointmentDataAtom);

  const user = useSelector((state: any) => state?.login?.user);

  // Check writeback support status
  const { data: hasWritebackSupport } = useHasWriteback(true);
  const submitWriteback = useSubmitWriteback();

  // Helper function to write back a note and invalidate the bulk query cache
  const writeBackNote = (note: ClinicalNote) => {
    submitWriteback.mutate(note.id, {
      onSuccess: () => {
        invalidateNotesCache();
      },
    });
  };

  // Component for formatting appointment dates
  const AppointmentDateDisplay = ({ date }: { date: string }) => {
    const formattedDate = useFormatAppointmentDate(date);
    return <div className="vai-text-sm text-gray-600">{formattedDate}</div>;
  };

  const getTreatments = (
    appointment: Appointment & {
      treatments: TTreatmentResponse[];
      notes: ClinicalNote[];
    },
  ) => {
    if (!appointment.treatments || appointment.treatments.length === 0) {
      return '--';
    }

    // Get unique treatment names from txGroupName
    // TODO: Move to a helper function
    const treatmentNames = [
      ...new Set(
        appointment.treatments
          .map(
            (treatment: TTreatmentResponse) =>
              treatment.txGroupName || treatment.txCode,
          )
          .filter(Boolean),
      ),
    ];

    return treatmentNames.join(', ');
  };

  // One or multiple notes can be recorded for an appointment
  // We display the first one; it's always the latest from BE
  const getNotePromptName = (
    appointment: Appointment & {
      treatments: TTreatmentResponse[];
      notes: ClinicalNote[];
    },
  ) => {
    if (!appointment.notes || appointment.notes.length === 0) {
      return '--';
    }

    // Get UI friendly template name for the first (most recent) note only
    const firstNote = appointment.notes[0];
    const templateName = displayHumanReadableTemplateName(firstNote);

    // Capitalize the template name from full caps
    return (
      templateName.charAt(0).toUpperCase() + templateName.slice(1).toLowerCase()
    );
  };

  const getUserDisplayName = (userData: any) => {
    if (userData?.firstName && userData?.lastName) {
      return `${capitalize(userData.firstName)} ${capitalize(
        userData.lastName,
      )}`;
    }
    if (userData?.lastName) {
      return capitalize(userData.lastName);
    }
    return 'Unknown';
  };

  const columns: TableColumn<
    VideaNotesAppointment & {
      treatments: TTreatmentResponse[];
      notes: ClinicalNote[];
    }
  >[] = [
    {
      key: 'patient',
      header: <Trans>Patient Info</Trans>,
      width: 'vai-w-[282px]',
      isSortable: true,
      render: (
        appointment: Appointment & {
          treatments: TTreatmentResponse[];
          notes: ClinicalNote[];
        },
        index: number,
      ) => (
        <PatientInfo
          appointment={appointment}
          index={index}
          context={PatientListPageLayoutContextMap.Ambient}
        />
      ),
    },
    {
      key: 'appointment',
      header: <Trans>Appointment Date</Trans>,
      width: 'vai-w-[211px]',
      isSortable: true,
      render: (
        appointment: Appointment & {
          treatments: TTreatmentResponse[];
          notes: ClinicalNote[];
        },
      ) => {
        return <AppointmentDateDisplay date={appointment.appointment.start} />;
      },
    },
    {
      key: 'treatments',
      header: <Trans>Treatments</Trans>,
      width: 'vai-w-[211px]',
      isSortable: true,
      render: (
        appointment: Appointment & {
          treatments: TTreatmentResponse[];
          notes: ClinicalNote[];
        },
      ) => (
        <div className="vai-text-sm text-gray-600">
          {getTreatments(appointment)}
        </div>
      ),
    },
    {
      key: 'noteTemplate',
      header: <Trans>Note Template</Trans>,
      width: 'vai-w-[200px]',
      render: (
        appointment: Appointment & {
          treatments: TTreatmentResponse[];
          notes: ClinicalNote[];
        },
      ) => (
        <div className="vai-text-sm text-gray-600">
          {getNotePromptName(appointment)}
        </div>
      ),
    },
    {
      key: 'recordedBy',
      header: <Trans>Recorded By</Trans>,
      width: 'vai-w-[200px]',
      render: (
        appointment: Appointment & {
          treatments: TTreatmentResponse[];
          notes: ClinicalNote[];
        },
      ) => {
        if (!appointment.notes || appointment.notes.length === 0) {
          return <div className="vai-text-sm text-gray-600">--</div>;
        }

        // Only show the first (most recent) note
        const firstNote = appointment.notes[0];
        const noteUser = firstNote.user;
        const recordedByName = getUserDisplayName(noteUser);

        const duration = firstNote.duration;
        const durationText =
          duration && duration > 0 ? formatTime(duration) : '';

        return (
          <div className="vai-flex vai-flex-col vai-gap-1">
            <div className="vai-text-sm text-gray-600">{recordedByName}</div>
            {durationText && (
              <div className="vai-flex vai-items-center vai-gap-1">
                <FontAwesomeIcon icon={faTimer} size="sm" />
                <span className="vai-text-sm">{durationText}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: <Trans>Note Actions</Trans>,
      width: 'vai-w-[200px]',
      render: (
        appointment: Appointment & {
          treatments: TTreatmentResponse[];
          notes: ClinicalNote[];
        },
      ) => {
        return (
          <AmbientTableActions
            user={user}
            appointment={appointment}
            notes={appointment.notes}
            hasWritebackSupport={!!hasWritebackSupport}
            isRecordingSessionActive={isRecordingSessionActive}
            currentRecordingAppointment={currentRecordingAppointment}
            submitWriteback={submitWriteback}
            openVideaNotes={openVideaNotes}
            writeBackNote={writeBackNote}
          />
        );
      },
    },
  ];

  return (
    <>
      <SortableTable
        data={appointments}
        columns={columns}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
        rowKey={(appointment, index) =>
          `${
            appointment.appointment.unifiedId ||
            (appointment as any).appointment.standalonePatientVisitId ||
            'unknown'
          }-${index}`
        }
        className="vai-h-[82vh]"
        scrollRef={scrollContainerRef}
        sentinelRef={sentinelRef}
        nextAppointmentRef={nextAppointmentRef}
      />
    </>
  );
};
